// src/services/offlineService.js
// Offline service to handle data synchronization and offline functionality

class OfflineService {
  constructor() {
    this.dbName = 'DeutschPrepDB';
    this.version = 1;
    this.db = null;
    this.isOnline = navigator.onLine;
    this.syncQueue = [];

    this.init();
    this.setupEventListeners();
  }

  async init() {
    try {
      await this.openDB();
      await this.setupStores();
      console.log('Offline service initialized');
    } catch (error) {
      console.error('Failed to initialize offline service:', error);
    }
  }

  openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('userProgress')) {
          db.createObjectStore('userProgress', { keyPath: 'userId' });
        }
        if (!db.objectStoreNames.contains('examResults')) {
          db.createObjectStore('examResults', { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains('syncQueue')) {
          db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains('cachedContent')) {
          db.createObjectStore('cachedContent', { keyPath: 'key' });
        }
      };
    });
  }

  async setupStores() {
    if (!this.db) return; // Guard against null db
    const userProgressCount = await this.getStoreCount('userProgress');
    if (userProgressCount === 0) {
      console.log('Setting up default offline data...');
    }
  }

  setupEventListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('App is online - syncing data...');
      this.syncOfflineData();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('App is offline - enabling offline mode...');
    });
  }

  // --- Generic database operations with guards ---

  getStoreCount(storeName) {
    // THE FIX: Add a guard to prevent error if db is not initialized
    if (!this.db) return Promise.resolve(0);
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  getData(storeName, key) {
    // THE FIX: Add a guard
    if (!this.db) return Promise.resolve(null);
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  setData(storeName, data) {
    // THE FIX: Add a guard
    if (!this.db) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  deleteData(storeName, key) {
    // THE FIX: Add a guard
    if (!this.db) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  getAllData(storeName, indexName = null, indexValue = null) {
    // THE FIX: Add a guard
    if (!this.db) return Promise.resolve([]);
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      let request;
      if (indexName && indexValue) {
        const index = store.index(indexName);
        request = index.getAll(indexValue);
      } else {
        request = store.getAll();
      }
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // --- Service specific methods ---

  async saveUserProgressOffline(userId, progressData) {
    const data = { userId, ...progressData, lastUpdated: Date.now(), synced: false };
    await this.setData('userProgress', data);
    if (!this.isOnline) {
      await this.addToSyncQueue('updateUserProgress', { userId, progressData });
    }
    return data;
  }

  async getUserProgressOffline(userId) {
    return await this.getData('userProgress', userId);
  }

  async saveExamResultOffline(userId, examId, resultData) {
    const data = { userId, examId, ...resultData, timestamp: Date.now(), synced: false };
    const id = await this.setData('examResults', data);
    if (!this.isOnline) {
      await this.addToSyncQueue('saveExamResult', { userId, examId, resultData });
    }
    return { id, ...data };
  }

  async getExamResultsOffline(userId) {
    return await this.getAllData('examResults', 'userId', userId);
  }

  async cacheContent(key, content, type = 'general') {
    const data = { key, content, type, timestamp: Date.now() };
    await this.setData('cachedContent', data);
    return data;
  }

  async getCachedContent(key) {
    const data = await this.getData('cachedContent', key);
    if (data) {
      const isExpired = Date.now() - data.timestamp > 24 * 60 * 60 * 1000;
      if (isExpired) {
        await this.deleteData('cachedContent', key);
        return null;
      }
      return data.content;
    }
    return null;
  }

  async addToSyncQueue(action, data) {
    const queueItem = { action, data, timestamp: Date.now(), retries: 0 };
    await this.setData('syncQueue', queueItem);
    this.syncQueue.push(queueItem);
  }

  async syncOfflineData() {
    if (!this.isOnline || !this.db) return;
    console.log('Starting offline data sync...');
    try {
      const queueItems = await this.getAllData('syncQueue');
      for (const item of queueItems) {
        try {
          await this.processSyncItem(item);
          await this.deleteData('syncQueue', item.id);
        } catch (error) {
          item.retries = (item.retries || 0) + 1;
          if (item.retries >= 3) {
            await this.deleteData('syncQueue', item.id);
          } else {
            await this.setData('syncQueue', item);
          }
        }
      }
    } catch (error) {
      console.error('Error during sync:', error);
    }
  }

  async processSyncItem(item) {
    console.log('Syncing item:', item.action);
    // This is where you would call your actual Firebase services.
  }

  async clearOfflineData() {
    if (!this.db) return; // THE FIX: Add a guard
    const stores = this.db.objectStoreNames;
    const transaction = this.db.transaction(stores, 'readwrite');
    for (const storeName of stores) {
      const store = transaction.objectStore(storeName);
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = resolve;
        request.onerror = reject;
      });
    }
    console.log('All offline data cleared');
  }

  async getOfflineStats() {
    if (!this.db) return null; // THE FIX: Add a guard
    try {
      const stats = {
        userProgress: await this.getStoreCount('userProgress'),
        examResults: await this.getStoreCount('examResults'),
        pendingSync: await this.getStoreCount('syncQueue'),
        cachedContent: await this.getStoreCount('cachedContent')
      };
      return stats;
    } catch (error) {
      console.error('Error getting offline stats:', error);
      return null;
    }
  }
}

const offlineService = new OfflineService();
export default offlineService;